import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { format } from 'date-fns';
import { invoicesApi } from '../../services/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return date;
  }
};

export default function SepaExportDialog({ open, onClose, currentAziendaId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filtri
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  // Data esecuzione addebiti (obbligatoria)
  const [executionDate, setExecutionDate] = useState(null);

  // Stato
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dati preview
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aziendaInfo, setAziendaInfo] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Carica preview fatture
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const filters = {};
      if (currentAziendaId) filters.azienda_id = currentAziendaId;
      if (dateFrom) filters.dateFrom = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) filters.dateTo = format(dateTo, 'yyyy-MM-dd');
      if (year) filters.year = year;

      const response = await invoicesApi.exportSepaPreview(filters);
      const data = response.data?.data || response.data;

      setInvoices(data.invoices || []);
      setSummary(data.summary || {});
      setAziendaInfo(data.azienda || {});

      // Preseleziona solo le fatture valide
      const validIds = (data.invoices || []).filter(i => i.valid).map(i => i.id);
      setSelectedIds(validIds);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [currentAziendaId, dateFrom, dateTo, year]);

  // Genera e scarica XML
  const handleGenerate = useCallback(async () => {
    if (!executionDate) {
      setError('Inserire la data di esecuzione addebiti');
      return;
    }
    if (selectedIds.length === 0) {
      setError('Selezionare almeno una fattura');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await invoicesApi.exportSepa({
        azienda_id: currentAziendaId || undefined,
        execution_date: format(executionDate, 'yyyy-MM-dd'),
        invoice_ids: selectedIds,
      });

      const data = response.data;

      // Download file XML
      const xmlBlob = new Blob([data.xmlContent], { type: 'application/xml' });
      const xmlUrl = window.URL.createObjectURL(xmlBlob);
      const xmlLink = document.createElement('a');
      xmlLink.href = xmlUrl;
      xmlLink.download = data.xmlFilename;
      document.body.appendChild(xmlLink);
      xmlLink.click();
      document.body.removeChild(xmlLink);
      window.URL.revokeObjectURL(xmlUrl);

      // Download file PDF resoconto (piccolo ritardo per evitare blocco browser)
      setTimeout(() => {
        const pdfBytes = Uint8Array.from(atob(data.pdfContent), c => c.charCodeAt(0));
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
        const pdfUrl = window.URL.createObjectURL(pdfBlob);
        const pdfLink = document.createElement('a');
        pdfLink.href = pdfUrl;
        pdfLink.download = data.pdfFilename;
        document.body.appendChild(pdfLink);
        pdfLink.click();
        document.body.removeChild(pdfLink);
        window.URL.revokeObjectURL(pdfUrl);
      }, 500);

      const txCount = data.stats?.transactionCount || selectedIds.length;
      const txTotal = data.stats?.totalAmount || '';
      setSuccess(`Distinta SEPA generata: ${txCount} addebiti${txTotal ? ` per ${formatCurrency(txTotal)}` : ''} — scaricati XML + PDF resoconto`);
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Errore nella generazione della distinta SEPA');
      }
    } finally {
      setGenerating(false);
    }
  }, [executionDate, selectedIds, currentAziendaId]);

  // Reset alla chiusura
  const handleClose = () => {
    setInvoices([]);
    setSummary(null);
    setAziendaInfo(null);
    setSelectedIds([]);
    setError(null);
    setSuccess(null);
    setExecutionDate(null);
    onClose();
  };

  // Colonne DataGrid
  const columns = [
    {
      field: 'nuf',
      headerName: 'N. Fatt.',
      width: isMobile ? 80 : 90,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.sezionale ? `${params.row.sezionale}-` : ''}
          {params.value}
        </Box>
      ),
    },
    {
      field: 'f_emission',
      headerName: 'Data',
      width: isMobile ? 85 : 100,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'customer_name',
      headerName: 'Cliente',
      flex: 1,
      minWidth: isMobile ? 100 : 180,
    },
    {
      field: 'balance',
      headerName: 'Saldo',
      width: isMobile ? 95 : 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
    },
    ...(isMobile ? [] : [
      {
        field: 'customer_iban',
        headerName: 'IBAN',
        width: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'mandato_sepa_id',
        headerName: 'Mandato',
        width: 120,
        renderCell: (params) => params.value || <Typography color="error" variant="body2">Mancante</Typography>,
      },
    ]),
    {
      field: 'valid',
      headerName: 'Stato',
      width: 80,
      align: 'center',
      renderCell: (params) => {
        if (params.value) {
          return <CheckIcon color="success" fontSize="small" />;
        }
        const warnings = params.row.warnings || [];
        return (
          <Tooltip title={warnings.join(', ')}>
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        );
      },
    },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BankIcon color="primary" />
          <Typography variant="h6">Distinta SEPA Direct Debit</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Warning campi mancanti azienda */}
        {aziendaInfo?.missingFields?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Configurazione SEPA incompleta.</strong> Campi mancanti nelle impostazioni azienda:{' '}
            {aziendaInfo.missingFields.join(', ')}.
            Vai in Impostazioni &gt; SEPA Direct Debit per configurarli.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Filtri */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Filtri e Data Esecuzione</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Anno</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Anno">
                  <MenuItem value="">Tutti</MenuItem>
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>

              <DatePicker
                label="Da data"
                value={dateFrom}
                onChange={setDateFrom}
                format="dd/MM/yyyy"
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
              <DatePicker
                label="A data"
                value={dateTo}
                onChange={setDateTo}
                format="dd/MM/yyyy"
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />

              <Divider orientation="vertical" flexItem />

              <DatePicker
                label="Data esecuzione addebiti *"
                value={executionDate}
                onChange={setExecutionDate}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                    required: true,
                    error: !executionDate && invoices.length > 0,
                    helperText: 'Data valuta addebito',
                  }
                }}
              />

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={loadPreview}
                disabled={loading}
                size="small"
              >
                Carica Fatture
              </Button>
            </Box>
          </LocalizationProvider>
        </Paper>

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Riepilogo */}
        {summary && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={`${summary.total} fatture trovate`}
                color="default"
                size="small"
              />
              <Chip
                icon={<CheckIcon />}
                label={`${summary.valid} valide`}
                color="success"
                variant="outlined"
                size="small"
              />
              {summary.invalid > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${summary.invalid} con problemi`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
              <Divider orientation="vertical" flexItem />
              <Chip
                label={`Selezionate: ${selectedIds.length}`}
                color="primary"
                size="small"
              />
              <Typography variant="subtitle2" color="primary">
                Totale: {formatCurrency(
                  invoices
                    .filter(i => selectedIds.includes(i.id))
                    .reduce((s, i) => s + (i.balance || 0), 0)
                )}
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Tabella fatture */}
        {invoices.length > 0 && (
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={invoices}
              columns={columns}
              checkboxSelection
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
              isRowSelectable={(params) => params.row.valid}
              density="compact"
              disableColumnMenu
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              getRowClassName={(params) => !params.row.valid ? 'sepa-row-invalid' : ''}
              sx={{
                '& .sepa-row-invalid': {
                  backgroundColor: 'action.hover',
                  opacity: 0.7,
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </Box>
        )}

        {/* Stato vuoto */}
        {!loading && invoices.length === 0 && summary === null && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BankIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              Clicca "Carica Fatture" per visualizzare le fatture dei clienti RID SEPA con saldo da incassare
            </Typography>
          </Box>
        )}

        {!loading && invoices.length === 0 && summary !== null && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Nessuna fattura trovata con i filtri selezionati per clienti con modalità pagamento RID SEPA
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Chiudi
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleGenerate}
          disabled={generating || selectedIds.length === 0 || !executionDate || aziendaInfo?.missingFields?.length > 0}
        >
          {generating ? 'Generazione...' : `Genera Distinta SEPA (${selectedIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
